<script setup>
// RM-359 PR 3 — Likelihood of Acceptance (Kapil and the product agent, 10/27):
// the band, large — Long Shot, Fair or High; how often RM requests in that
// band at that kind of restaurant were accepted in the last 12 months; what's
// strong about the request; what could improve its chances. Words come from
// the server (the RM AI replies table, lib/requestLikelihood.render). Used on
// the request's "Before you submit" step and above RM AI's Review & Submit
// button in the chat. It never blocks anything.
const props = defineProps({
  likelihood: { type: Object, default: null },
  loading: { type: Boolean, default: false },
})

const BAND_TONE = { High: 'high', Fair: 'fair', 'Long Shot': 'long-shot' }
const tone = computed(() => BAND_TONE[props.likelihood?.band] || 'fair')
</script>

<template>
  <section
    v-if="loading || likelihood"
    class="likelihood"
    aria-live="polite"
  >
    <template v-if="loading && !likelihood">
      <VSkeletonLoader type="heading, text, text" />
    </template>
    <template v-else>
      <p class="likelihood__eyebrow">
        Likelihood of acceptance
      </p>
      <p
        class="likelihood__band"
        :class="`likelihood__band--${tone}`"
      >
        {{ likelihood.band }}
      </p>
      <p
        v-if="likelihood.historical"
        class="likelihood__history"
      >
        {{ likelihood.historical }}
      </p>
      <template v-if="likelihood.strengths?.length">
        <p class="likelihood__section">
          {{ likelihood.strengthsTitle }}
        </p>
        <ul class="likelihood__list">
          <li
            v-for="(s, i) in likelihood.strengths"
            :key="`s${i}`"
          >
            {{ s }}
          </li>
        </ul>
      </template>
      <template v-if="likelihood.improvements?.length">
        <p class="likelihood__section">
          {{ likelihood.improvementsTitle }}
        </p>
        <ul class="likelihood__list">
          <li
            v-for="(s, i) in likelihood.improvements"
            :key="`i${i}`"
          >
            {{ s }}
          </li>
        </ul>
      </template>
    </template>
  </section>
</template>

<style lang="scss" scoped>
.likelihood {
  border: 1px solid var(--ds-border-light);
  border-radius: 12px;
  background: var(--ds-surface-card);
  padding: 16px;
  margin-bottom: 16px;
}

.likelihood__eyebrow {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--ds-text-secondary);
}

.likelihood__band {
  margin: 2px 0 8px;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.15;
}

.likelihood__band--high {
  color: rgb(var(--v-theme-success));
}

.likelihood__band--fair {
  color: rgb(var(--v-theme-primary));
}

.likelihood__band--long-shot {
  color: rgb(var(--v-theme-warning));
}

.likelihood__history {
  margin: 0 0 4px;
  font-size: 14px;
  line-height: 1.45;
  color: var(--ds-text-primary);
}

.likelihood__section {
  margin: 12px 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: var(--ds-text-strong);
}

.likelihood__list {
  margin: 0;
  padding-left: 20px;
  list-style: disc;
  font-size: 14px;
  line-height: 1.45;
  color: var(--ds-text-secondary);

  li + li {
    margin-top: 4px;
  }
}
</style>
